import { useEffect, useMemo, useState } from 'react';
import { Symbol } from './symbol';

/**
 * Page-level table search: one box above each list that matches against every
 * column the table shows.
 *
 * Matching is token-based and case-insensitive: "madan placed" finds a row
 * whose customer is Madan and whose status is Placed. Digits are also compared
 * with separators stripped, so "98861" finds "+91 98861 51913" and "1725"
 * finds "₹1,725".
 *
 * Client-side matching only sees rows already loaded. Lists paged by the API
 * must also send the query to the server (debounced with `useDebouncedValue`)
 * so a match on page 7 is found.
 */

function normalise(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toLocaleString('en-IN');
  if (Array.isArray(value)) return value.map(normalise).join(' ');
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).map(normalise).join(' ');
  return String(value);
}

/** True when every whitespace-separated token of `query` appears in `fields`. */
export function matchesQuery(fields: unknown[], query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = fields.map(normalise).join(' ').toLowerCase();
  const digits = haystack.replace(/[^0-9]/g, '');
  // Enum-style values ("OUT_FOR_DELIVERY") should match "out for delivery".
  const spaced = haystack.replace(/_/g, ' ');
  return q.split(/\s+/).every((token) => {
    if (haystack.includes(token) || spaced.includes(token)) return true;
    const tokenDigits = token.replace(/[^0-9]/g, '');
    return tokenDigits.length >= 3 && tokenDigits === token.replace(/[\s,₹+-]/g, '') && digits.includes(tokenDigits);
  });
}

/**
 * Filters `rows` by `query` across the fields `fieldsOf` returns for each row.
 * Pass the values the table renders (formatted dates, labels), not just ids.
 */
export function useTableSearch<T>(rows: readonly T[] | undefined, fieldsOf: (row: T) => unknown[]) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => (rows ?? []).filter((r) => matchesQuery(fieldsOf(r), query)),
    // fieldsOf is usually an inline arrow; rows + query are what change results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, query],
  );
  return { query, setQuery, filtered };
}

/** `value`, but only after it has stopped changing for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  /** Name what can be searched, e.g. "Search by order, customer, phone or status". */
  placeholder?: string;
  className?: string;
  /** "12 of 40" style count, shown at the right while a query is active. */
  resultCount?: number;
}

/** The v2 search pill with a clear button. */
export function TableSearch({ value, onChange, placeholder = 'Search this table', className = '', resultCount }: TableSearchProps) {
  return (
    <div className={`flex min-w-0 items-center gap-3 ${className}`}>
      <label className="relative block w-full sm:max-w-[400px]">
        <span className="sr-only">{placeholder}</span>
        <Symbol
          name="search"
          size={18}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500"
        />
        <input
          type="search"
          className="search-pill !pr-9"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onChange('');
          }}
        />
        {value && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-ink-500 hover:bg-ink-200 hover:text-ink-900"
          >
            <Symbol name="close" size={16} />
          </button>
        )}
      </label>
      {value && resultCount !== undefined && (
        <span className="shrink-0 text-[13px] text-ink-500" aria-live="polite">
          {resultCount} {resultCount === 1 ? 'match' : 'matches'}
        </span>
      )}
    </div>
  );
}
